import React, { useState } from "react";
import { connect } from "react-redux";
import { useStripe } from "@stripe/react-stripe-js";
import {
  orderPaymentRedirect,
  orderVatRegRedirect,
  orderPaymentAddonsRedirect,
  orderPaymentChargeInvoiceRedirect,
  orderSalesTaxRedirect,
  changeCardRedirect,
  removeCard,
} from "../../redux/features/payment/paymentSlice";
import { ButtonGroup, Button } from "@shopify/polaris";
import Modal from "components/Modal";
import { createCustomDispatch } from "helpers/customDispatch";
import { clearCompanyPaymentCard } from '../../redux/features/user/userSlice'
import BannerCustom from "components/Banners/BannerCustom";

const CheckoutForm = (props) => {
  const stripe = useStripe();
  const [paymentShopify, setPaymentShopify] = useState(null);
  const [paymentError, setPaymentError] = useState(false);
  const [delError, setDelError] = useState(false);
  const [save] = useState(true);
  const [actionDialogs, setActionDialogs] = useState({
    deleteCard: { open: false },
  });

  const reportPaymentError = (message) => {
    if (!message) {
      return;
    }
    setPaymentError(message);
    props.onPaymentError?.(message);
  };

  const doPayment = (resp) => {
    if (!stripe) {
      reportPaymentError("Stripe is not ready. Please wait a moment and try again.");
      return;
    }

    stripe
      .redirectToCheckout({
        sessionId: resp.session_id,
      })
      .then((strieResp) => {
        if (strieResp.error) {
          reportPaymentError(strieResp.error.message);
        }
      })
      .catch((error) => {
        reportPaymentError(error.message || "Payment redirect failed. Please try again.");
      });
  };

  const doRemovePaymentMethod = () => {
    const { t } = props;

    props
      .removeCard()
      .then((result) => {
        if (result?.error) {
          setDelError(t("vatReg.err"));
          return;
        }
        props.clearCompanyPaymentCard();
        handleActionDialogsClose("deleteCard");
        setDelError(false);
      })
      .catch(() => {
        setDelError(t("vatReg.err"));
      });
  };

  const handleActionDialogsClose = (name) => {
    setActionDialogs(prev => {
      const newDialogs = { ...prev };
      newDialogs[name] = { ...newDialogs[name], open: false };
      newDialogs.cellData = {};
      return newDialogs;
    });
  };

  const handleActionDialogsOpen = (name, data = {}) => {
    setActionDialogs(prev => {
      const newDialogs = { ...prev };
      newDialogs[name] = { ...newDialogs[name], open: true };
      newDialogs.cellData = data;
      return newDialogs;
    });
  };

  const handleChange = () => {
    const { t } = props;

    props
      .changeCardRedirect()
      .then((resp) => doPayment(resp))
      .catch((err) => {
        reportPaymentError(err.error || t("vatReg.err"));
      });
  };

  const handleSubmit = () => {
    const { data: params, service, promoCode, t, selectedPlan } = props;
    const data = {
      ...params,
      save,
      promo_code: promoCode,
    };

    if (data?.type === 'sales_tax_subs' && data?.country) {
      if (typeof data.country === 'string') {
        if (data.country === 'USA' || data.country === 'All') {
          data.country = {
            code: 'USA',
            name: 'United States'
          };
          data.countries_name = 'United States';
        } else {

          const stateObj = selectedPlan?.select_countries?.find(state => state.code === data.country);
          const stateName = stateObj?.name || data.country;
          data.country = {
            code: data.country,
            name: stateName
          };
          data.countries_name = stateName;
        }
      } else if (data.country && typeof data.country === 'object') {
        data.countries_name = data.country.name || data.country.code;
      }
    }
    
    if (service === "VAT registration") {
      props
        .orderVatRegRedirect({ data })
        .then((resp) => doPayment(resp))
        .catch((err) => {
          setPaymentError(err.error || t("vatReg.err"));
        });
    } else if (service === "addons") {
      props
        .orderPaymentAddonsRedirect({ data })
        .then((resp) => {
          if (resp.session_id) {
            doPayment(resp);
          } else {
            setPaymentShopify(true);
          }
        })
        .catch((err) => {
          setPaymentError(err.error || t("vatReg.err"));
        });
    } else if (service === "charge") {
      props
        .orderPaymentChargeInvoiceRedirect({ data })
        .then((resp) => doPayment(resp))
        .catch((err) => {
          setPaymentError(err.error || t("vatReg.err"));
        });
    } else if (service === "tax-return") {
      props
        .orderSalesTaxRedirect({ data })
        .then((resp) => {
          if (data.action === "credits" && resp.status === 200) {
            props.onCreditsPayment();
          } else {
            doPayment(resp);
          }
        })
        .catch((err) => {
          setPaymentError(err.error || t("vatReg.err"));
        });
    } else {
      props
        .orderPaymentRedirect({ data })
        .then((resp) => {
          if (resp.session_id) {
            doPayment(resp);
          } else {
            setPaymentShopify(resp.message);
          }
        })
        .catch((err) => {
          setPaymentError(
            t("vatReg.err") + `${err.error ? `: ${err.error}` : ""}`
          );
        });
    }
  };

  const renderDeleteCardDialog = () => {
    const { removingCard, t } = props;

    const onClose = () => {
      handleActionDialogsClose("deleteCard");
      setDelError(false);
    };
  
    return (
      <Modal
        title={"Remove payment method"}
        visible={actionDialogs.deleteCard.open}
        footer={
         <ButtonGroup fullWidth>
            <Button onClick={onClose} size="large">
              {t("profileUsers.cancel")}
            </Button>
            <Button
              loading={removingCard}
              tone="critical"
              variant="plain"
              onClick={doRemovePaymentMethod}
              size="large"
            >
              {t("profileMemb.confirm")}
            </Button>
         </ButtonGroup>
        }
        iconType='danger'
        onClose={onClose}
        content={
          <>
            {delError && (
              <div>
                <BannerCustom
                  status='critical'
                  message={delError}
                  onDismiss={() => setDelError(false)}
                />
                <br />
              </div>
            )}
            Are you sure you want to remove the card?
          </>
        }
      />
    );
  };

  const { service, editingCard, user, renderCustom, renderChangeCustom, onPaymentError } = props;

  return (
    <div style={{width:'100%'}}>
      <form style={{width:'100%'}}>
        {paymentError && !renderChangeCustom ? (
          <div>
            <BannerCustom
              status='critical'
              message={paymentError}
              onDismiss={() => {
                setPaymentError(false);
                onPaymentError?.(null);
              }}
            />
            <br />
          </div>
        ) : null}

        {paymentShopify && (
          <div>
            <BannerCustom
              status='success'
              message={paymentShopify}
              onDismiss={() => setPaymentShopify(null)}
            />
            <br />
          </div>
        )}

        {service === "change" ? (
          renderChangeCustom ? (
            renderChangeCustom({
              onChangeCard: handleChange,
              loading: editingCard,
            })
          ) : (
            <ButtonGroup>
              <Button
                variant='primary'
                size={'micro'}
                submit
                loading={editingCard}
                onClick={(e) => {
                  e.preventDefault();
                  handleChange();
                }}
              >
                {user.company && user.company.info.card ? "Edit" : "Add card"}
              </Button>
              {user.company && user.company.info.card && (
                <Button
                  tone={'critical'}
                  variant={'plain'}
                  size={'micro'}
                  onClick={() => handleActionDialogsOpen("deleteCard")}
                >
                  Remove
                </Button>
              )}
            </ButtonGroup>
          )
        ) : (
          <>
            {renderCustom ? (
              renderCustom((e) => {
                e.preventDefault();
                handleSubmit();
              }) // passing onConfirm fn
            ) : (
              <ButtonGroup fullWidth>
                <div style={{width:'100%'}}>
                  <Button onClick={props.onClose} size='large'>
                    Cancel
                  </Button>
                </div>

                <Button
                  variant='primary'
                  submit
                  size='large'
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  Confirm
                </Button>
              </ButtonGroup>
            )}
          </>
        )}
      </form>

      {renderDeleteCardDialog()}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  defaultLanguage: state.user.defaultLanguage,
  editingCard: state.payment.editingCardRedirect,
  removingCard: state.payment.removingCard,
});

const mapDispatchToProps = (defaultDispatch) => {
  const dispatch = createCustomDispatch(defaultDispatch);

  return {
    clearCompanyPaymentCard: () => dispatch(clearCompanyPaymentCard()),
    orderPaymentRedirect: (params) => dispatch(orderPaymentRedirect(params)),
    orderVatRegRedirect: (params) => dispatch(orderVatRegRedirect(params)),
    orderPaymentAddonsRedirect: (data) =>
      dispatch(orderPaymentAddonsRedirect(data)),
    orderPaymentChargeInvoiceRedirect: (params) =>
      dispatch(orderPaymentChargeInvoiceRedirect(params)),
    orderSalesTaxRedirect: (params) => dispatch(orderSalesTaxRedirect(params)),
    changeCardRedirect: () => dispatch(changeCardRedirect()),
    removeCard: () => dispatch(removeCard()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CheckoutForm);
